/**
 * LoginScreen — staff login for the admin web shell (ODE-P01-T10).
 *
 * Submits staffId + credential to POST /v1/admin/auth/login (real staff
 * credential store, packages/db migration 0008). Outcomes:
 *   success → session stored in memory, navigate to the dashboard
 *   401     → inline error (uniform; reason withheld)
 *   503     → "authentication unavailable" error (fail closed)
 *   network → generic error
 * While submitting the form is disabled (no double submit; REQ-090 loading).
 *
 * Traces to: ODE-PRD-REQ-077, ODE-PRD-REQ-090.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiRequestError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';

type LoginError = { readonly code: string; readonly message: string } | null;

export function LoginScreen(): JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [credential, setCredential] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<LoginError>(null);

  const onSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await login(staffId, credential);
      navigate('/', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        if (caught.status === 401) {
          setError({ code: 'UNAUTHORIZED', message: 'Kredensial tidak valid.' });
        } else if (caught.status === 503) {
          setError({
            code: 'AUTH_UNAVAILABLE',
            message: 'Layanan autentikasi tidak tersedia. Coba lagi nanti.',
          });
        } else {
          setError({ code: caught.code, message: caught.message });
        }
      } else {
        setError({ code: 'NETWORK', message: 'Gagal terhubung ke server.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="login" aria-labelledby="login-title">
      <h1 id="login-title">ODE Admin</h1>
      <p>Masuk dengan kredensial staf.</p>
      <form onSubmit={onSubmit} noValidate={false}>
        <label>
          Staff ID
          <input
            data-testid="staff-id"
            value={staffId}
            onChange={(event) => setStaffId(event.target.value)}
            autoComplete="username"
            required
          />
        </label>
        <label>
          Kredensial
          <input
            data-testid="credential"
            type="password"
            value={credential}
            onChange={(event) => setCredential(event.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        <button type="submit" data-testid="login-submit" disabled={submitting}>
          {submitting ? 'Memuat…' : 'Masuk'}
        </button>
      </form>
      {error !== null && (
        <section role="alert" data-testid="login-error" className="state state-error">
          <h3>{error.code === 'AUTH_UNAVAILABLE' ? 'Layanan tidak tersedia' : 'Gagal masuk'}</h3>
          <p>{error.message}</p>
        </section>
      )}
    </main>
  );
}